import type { RoutineAction } from "@/api";

/** A Routine being executed; persisted so a reload resumes the timer. */
export type RoutineRun = {
  activityId: string;
  routineTitle: string;
  items: RoutineAction[];
  /** Index of the current item. */
  index: number;
  /** Wall-clock ms when the current item started (shifted on resume). */
  itemStartedAt: number;
  /** Wall-clock ms when paused, or null while running. */
  pausedAt: number | null;
  startedAt: number;
};

const KEY = "leno.routine.run";

export function loadRun(): RoutineRun | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const run = JSON.parse(raw) as RoutineRun;
    if (!run.activityId || !Array.isArray(run.items) || run.items.length === 0)
      return null;
    return run;
  } catch {
    return null;
  }
}

export function saveRun(run: RoutineRun | null) {
  try {
    if (run) localStorage.setItem(KEY, JSON.stringify(run));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: the timer still works for this page load.
  }
}

/** Milliseconds left on the current item at `now`, never negative. */
export function remainingMs(run: RoutineRun, now: number): number {
  const at = run.pausedAt ?? now;
  const length = run.items[run.index].duration_minutes * 60_000;
  return Math.max(0, length - (at - run.itemStartedAt));
}

/**
 * Advance past items whose time has elapsed. Carries the overshoot into the
 * next item so the overall schedule does not drift. Returns the new run, or
 * null when every item is finished.
 */
export function advance(run: RoutineRun, now: number): RoutineRun | null {
  let r = run;
  while (r.pausedAt === null) {
    const length = r.items[r.index].duration_minutes * 60_000;
    const elapsed = now - r.itemStartedAt;
    if (elapsed < length) return r;
    if (r.index + 1 >= r.items.length) return null;
    r = { ...r, index: r.index + 1, itemStartedAt: r.itemStartedAt + length };
  }
  return r;
}

export function formatClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
